"use client";

import { useEffect, useState } from "react";
import { Button } from "@workspace/ui/components";
import { cn } from "@workspace/ui/lib/utils";
import {
  DownloadStage,
  type StageAction,
  type StageTone,
} from "./download-stage";
import { AppleIcon, WindowsIcon } from "./platform-icons";
import { useFileDownload } from "./use-file-download";
import {
  DESKTOP_SETUP_GUIDE_URL,
  type DesktopBuild,
  type DesktopBuildId,
  type DesktopRelease,
  detectBuildId,
  downloadInBrowser,
  formatBytes,
  formatEta,
  formatProgressBytes,
  isDesktopDevice,
  SUPPORT_EMAIL,
  SYSTEM_REQUIREMENTS,
} from "./releases";

type Phase =
  /** Only until hydration runs — the platform check itself is synchronous. */
  | "detecting"
  | "ready"
  /** Phone or tablet — the installer cannot run here. */
  | "unsupported"
  /** The download hook owns the copy from here on. */
  | "active";

interface StageCopy {
  tone?: StageTone;
  headline: string;
  subline: string;
  progress?: number;
  primary?: StageAction | null;
  secondary?: StageAction | null;
}

export function DownloadSection({ release }: { release: DesktopRelease }) {
  const [phase, setPhase] = useState<Phase>("detecting");
  // null until detection runs, and for desktops we ship no build for.
  const [buildId, setBuildId] = useState<DesktopBuildId | null>(null);
  const [platformsOpen, setPlatformsOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [downloadStarted, setDownloadStarted] = useState(false);
  /**
   * The in-page progress experience needs CORS on the downloads bucket. Until
   * that lands the button hands the file to the browser, and the progress flow
   * can still be exercised with /app?progress=1.
   */
  const [progressMode, setProgressMode] = useState(false);
  const download = useFileDownload();

  const build = release.builds.find((item) => item.id === buildId) ?? null;
  const activeBuild = download.build ?? build ?? release.builds[0]!;

  // Detection needs the browser, so the first paint shows the default build.
  useEffect(() => {
    setBuildId(detectBuildId());
    setPhase(isDesktopDevice() ? "ready" : "unsupported");
    setProgressMode(
      new URLSearchParams(window.location.search).has("progress"),
    );
  }, []);

  const startFromClick = (target: DesktopBuild) => {
    setBuildId(target.id);

    if (!progressMode) {
      // The browser owns the transfer from here, so there is no success signal
      // to report — the copy below promises nothing we cannot see.
      downloadInBrowser(target);
      setDownloadStarted(true);
      return;
    }

    setPhase("active");
    download.start(target);
  };

  const cancel = () => {
    download.cancel();
    setPhase("ready");
  };

  const copyPageLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
    } catch {
      setLinkCopied(false);
    }
  };

  const stageVisible =
    phase === "unsupported" || (progressMode && phase === "active");
  const platformsVisible =
    platformsOpen ||
    phase === "unsupported" ||
    (progressMode &&
      (download.status === "error" || download.status === "interrupted"));
  // Nothing to put on the button when we do not know which build fits.
  const heroCtaVisible =
    phase === "detecting" || (phase === "ready" && build !== null);
  const platformLinkVisible = !platformsVisible && heroCtaVisible;
  const showAllPlatforms =
    platformsVisible || (phase === "ready" && build === null);

  const stage = getStageCopy({
    phase,
    activeBuild,
    download,
    linkCopied,
    onStart: startFromClick,
    onResume: download.resume,
    onCancel: cancel,
    onCopyLink: copyPageLink,
  });

  // No readable release means no file names, so a download button would only
  // send people to a 404. Say so instead of pretending.
  if (!release.available) {
    return (
      <DownloadStage
        headline="Downloads are temporarily unavailable"
        subline="We're publishing a new build — this usually takes a few minutes. Try again shortly."
        primary={{ label: "Email us", href: `mailto:${SUPPORT_EMAIL}` }}
      />
    );
  }

  return (
    <div className="flex w-full flex-col items-center gap-10 lg:gap-10">
      <div
        className={cn(
          "flex w-full flex-col items-center gap-3 lg:gap-3.5",
          heroCtaVisible && "min-h-[96px]",
        )}
      >
        {heroCtaVisible && (
          <Button
            variant="d-primary"
            disabled={!build}
            onClick={() => build && startFromClick(build)}
            className="h-auto w-full justify-center gap-2.5 rounded-[14px] px-5 py-[17px] text-[18px] font-bold leading-[22px] shadow-[0_0_0_2px_#fff,0_4px_8px_rgba(41,37,36,0.14)] disabled:bg-[color:#FFD602] disabled:opacity-100 lg:w-auto lg:gap-3 lg:rounded-[16px] lg:px-8 lg:py-[18px] lg:text-[22px] lg:leading-[26px] lg:shadow-[0_0_0_2px_#fff,0_6px_12px_rgba(41,37,36,0.14)] [&_svg]:size-[19px] lg:[&_svg]:size-[22px]"
          >
            {phase === "detecting" || !build ? (
              <>
                <span className="size-[17px] shrink-0 animate-spin rounded-full border-[3px] border-[rgba(41,37,36,0.22)] border-t-d-black lg:size-5" />
                Detecting your device…
              </>
            ) : (
              <>
                {build.os === "mac" ? <AppleIcon /> : <WindowsIcon />}
                Download for {build.label}
              </>
            )}
          </Button>
        )}

        <span className="text-center text-sm font-medium leading-5 text-d-gray lg:text-base lg:leading-[22px]">
          {downloadStarted
            ? "Your download should start automatically — check your downloads folder."
            : getMetaLine({
                phase,
                build,
                activeBuild,
                download,
                version: release.version,
              })}
        </span>

        {platformLinkVisible && (
          <Button
            type="button"
            variant="link"
            onClick={() => setPlatformsOpen(true)}
            className="text-sm font-semibold leading-[18px] text-d-gray underline underline-offset-[3px] transition-colors hover:text-d-black lg:text-[15px] lg:leading-5"
          >
            Not your platform?
          </Button>
        )}
      </div>

      {stageVisible && <DownloadStage {...stage} />}

      {showAllPlatforms && (
        <div className="flex w-full flex-col items-center gap-4 pt-2 lg:gap-5">
          <div className="flex w-full items-center gap-3 lg:max-w-[560px] lg:gap-4">
            <div className="h-px flex-1 bg-[#E3D8C4]" />
            <span className="whitespace-nowrap text-sm font-medium leading-[18px] text-d-gray lg:text-base lg:leading-[22px]">
              or choose your platform
            </span>
            <div className="h-px flex-1 bg-[#E3D8C4]" />
          </div>

          <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-row lg:gap-4">
            {release.builds.map((item) => (
              <Button
                key={item.id}
                variant="d-default"
                onClick={() => startFromClick(item)}
                className={cn(
                  "h-auto w-full justify-start gap-3 has-[svg]:px-6 py-3.5",
                  "lg:w-auto lg:px-[22px] [&_svg]:size-[19px] lg:[&_svg]:size-5",
                )}
              >
                {item.os === "mac" ? <AppleIcon /> : <WindowsIcon />}
                <span className="flex flex-col items-start gap-0.5">
                  <span className="text-[17px] font-semibold leading-[21px] lg:text-[18px] lg:leading-[22px]">
                    {item.label}
                  </span>
                  <span className="text-[13px] font-medium leading-4 text-d-gray">
                    {item.hint}
                  </span>
                </span>
              </Button>
            ))}
          </div>

          <span className="text-center text-sm font-medium leading-[18px] text-d-gray lg:text-base lg:leading-[22px]">
            Available on {SYSTEM_REQUIREMENTS}
            {release.version ? ` · Version ${release.version}` : ""}
          </span>
        </div>
      )}
    </div>
  );
}

const versionAndSize = (build: DesktopBuild, version: string | null) =>
  [
    version ? `Version ${version}` : null,
    build.sizeBytes ? formatBytes(build.sizeBytes) : null,
  ]
    .filter(Boolean)
    .join(" · ") || SYSTEM_REQUIREMENTS;

function getMetaLine({
  phase,
  build,
  activeBuild,
  download,
  version,
}: {
  phase: Phase;
  build: DesktopBuild | null;
  activeBuild: DesktopBuild;
  download: ReturnType<typeof useFileDownload>;
  version: string | null;
}) {
  if (phase === "unsupported") {
    return `Yolk Desktop runs on ${SYSTEM_REQUIREMENTS}`;
  }

  if (phase === "active" && download.status !== "idle") {
    return [version ? `Version ${version}` : null, activeBuild.label]
      .filter(Boolean)
      .join(" · ");
  }

  if (!build) return `Yolk Desktop runs on ${SYSTEM_REQUIREMENTS}`;

  return versionAndSize(build, version);
}

function getStageCopy({
  phase,
  activeBuild,
  download,
  linkCopied,
  onStart,
  onResume,
  onCancel,
  onCopyLink,
}: {
  phase: Phase;
  activeBuild: DesktopBuild;
  download: ReturnType<typeof useFileDownload>;
  linkCopied: boolean;
  onStart: (build: DesktopBuild) => void;
  onResume: () => void;
  onCancel: () => void;
  onCopyLink: () => void;
}): StageCopy {
  if (phase === "unsupported") {
    return {
      headline: "This device can't run Yolk Desktop",
      subline:
        "Copy the link and finish the install on your Mac or PC — the builds are listed below.",
      primary: {
        label: linkCopied ? "Link copied" : "Copy link",
        onClick: onCopyLink,
      },
    };
  }

  if (download.status === "error") {
    return {
      tone: "failed",
      headline: "Download didn't start",
      subline:
        "Your browser blocked the file, or the connection dropped. Try again — or pick a build below.",
      primary: { label: "Try again", onClick: () => onStart(activeBuild) },
      secondary: { label: "Download directly", href: activeBuild.url },
    };
  }

  if (download.status === "interrupted") {
    const stoppedAt =
      download.progress !== null ? ` at ${Math.floor(download.progress)}%` : "";
    return {
      tone: "failed",
      headline: `Download stopped${stoppedAt}`,
      subline: `The connection dropped after ${formatBytes(download.receivedBytes)}. Resume where it left off, or start over.`,
      progress: download.progress ?? undefined,
      primary: { label: "Resume download", onClick: onResume },
      secondary: { label: "Start over", onClick: () => onStart(activeBuild) },
    };
  }

  if (download.status === "completed") {
    const finish =
      activeBuild.os === "mac"
        ? "and drag Yolk into Applications"
        : "and run the installer";
    return {
      tone: "done",
      headline: "Download complete",
      subline: `Open ${activeBuild.fileName} from your downloads folder ${finish}.`,
      primary: { label: "Download again", onClick: () => onStart(activeBuild) },
      secondary: DESKTOP_SETUP_GUIDE_URL
        ? { label: "Setup guide", href: DESKTOP_SETUP_GUIDE_URL }
        : null,
    };
  }

  if (download.status === "preparing") {
    return {
      tone: "working",
      headline: "Downloading Yolk Desktop",
      subline: `${activeBuild.fileName} · starting…`,
      progress: 0,
      secondary: { label: "Cancel", onClick: onCancel },
    };
  }

  const eta =
    download.secondsLeft !== null ? formatEta(download.secondsLeft) : null;
  // Without Content-Length we can only report what has arrived so far.
  const transferred = download.totalBytes
    ? formatProgressBytes(download.receivedBytes, download.totalBytes)
    : `${formatBytes(download.receivedBytes)} downloaded`;

  return {
    tone: "working",
    headline: "Downloading Yolk Desktop",
    subline: [activeBuild.fileName, transferred, eta]
      .filter(Boolean)
      .join(" · "),
    progress: download.progress ?? undefined,
    secondary: { label: "Cancel", onClick: onCancel },
  };
}
